import { useState } from 'react'
import CustomModal from 'components/modals/CustomModal'

import { useTranslation } from 'react-i18next'
import { BUTTONS, FORM, PLACEHOLDERS, TABLE } from 'locales/locales'
import { requiredValidation } from 'utils/helpers/validations'
import axiosApi from 'app/axiosApi'
import SelectImage from 'components/input/select-input/SelectImage'
import CustomText from 'components/text/CustomText'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { AvatarSize } from 'components/avatar/Avatar'
import colors from 'utils/colors'
import styled from 'styled-components'
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs'

interface ICreateUserModalProps {
  inAppBrowser: {
    _id: string
    name: string
    url: string
    isActive: boolean
    isExtraTab: boolean
    inAppBrowserLogo: string
  }
  onClose: () => void
  fetchSystemSettings: () => Promise<void>
}

const InAppBrowserEditModal = ({ inAppBrowser, fetchSystemSettings, onClose }: ICreateUserModalProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [generalInfo, setGeneralInfo] = useState({ ...inAppBrowser })
  const [selectedFile, setSelectedFile] = useState<FileList>()

  const onChangeInput = (value: string | boolean, key: string) => {
    setGeneralInfo({ ...generalInfo, [key]: value })
  }

  const onConfirmOk = async () => {
    const { isValid } = requiredValidation(generalInfo.name)
    if (!isValid) {
      onClose()
      return
    }

    const params = {
      _id: generalInfo?._id,
      name: generalInfo?.name.trim(),
      url: generalInfo?.url.trim(),
      isActive: generalInfo?.isActive || false,
      isExtraTab: generalInfo?.isExtraTab || false,
      hasAvatar: !!generalInfo?.inAppBrowserLogo,
    }
    const files = selectedFile && selectedFile.length > 0 ? Array.from(selectedFile) : []
    const url = `files/upload?from=inAppBrowser&inAppBrowserId=${generalInfo?._id}`
    try {
      await axiosApi.uploadFile(url, files, 'file',{params: params}, undefined)
      onClose()
      await fetchSystemSettings()
    } finally {
      setIsLoading(false)
    }
    onClose()
  }

  const uploadAvatar = async (fileList: FileList) => {
    const fileUrl = URL.createObjectURL(fileList[0])
    setSelectedFile(fileList)
    setGeneralInfo({ ...generalInfo, inAppBrowserLogo: fileUrl })
  }

  return (
    <>
      <CustomModal
        closeModal={onClose}
        title={generalInfo.name}
        onCancel={onClose}
        onOk={onConfirmOk}
        loading={isLoading}
      >
        <InfoModalWrapper>
          <SelectImage
            type="inAppBrowser"
            avatar={generalInfo.inAppBrowserLogo}
            text={generalInfo.name}
            avatarSize={AvatarSize.biggest}
            buttonText={BUTTONS.UPLOAD}
            loading={isLoading}
            onSelectPhoto={uploadAvatar}
          />
          <FormWrapper>
            <CustomFormInput
              value={generalInfo.name}
              title={t(FORM.NAME) as string}
              placeholder={t(PLACEHOLDERS.IN_APP_BORWSER_TITLE)}
              onChange={(value: string) => onChangeInput(value, 'name')}
            />
            <CustomFormInput
              value={generalInfo.url}
              title={'URL'}
              placeholder={'URL'}
              onChange={(value: string) => onChangeInput(value, 'url')}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CustomText typography="body3" color={colors.gray80}>
                {t('STATUS')}
              </CustomText>
              <Checkbox
                checked={generalInfo.isActive}
                style={{border: `1px solid ${colors.vime}`}}
                onChange={(event: CheckboxChangeEvent) => onChangeInput(event.value, 'isActive')}
              />
              <CustomText typography="body3" color={colors.gray80}>
                {t(TABLE.SHOW_IN_MENU)}
              </CustomText>
              <Checkbox
                checked={generalInfo.isExtraTab}
                style={{border: `1px solid ${colors.vime}`}}
                onChange={(event: CheckboxChangeEvent) => onChangeInput(event.value, 'isExtraTab')}
              />
            </div>
          </FormWrapper>
        </InfoModalWrapper>
      </CustomModal>
    </>
  )
}

export default InAppBrowserEditModal

const InfoModalWrapper = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`